import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Prompt } from '../types';

interface RelatedPromptsProps {
  currentPrompt: Prompt;
  allPrompts: Prompt[];
  maxItems?: number;
  onPromptClick?: (prompt: Prompt) => void;
}

const RelatedPrompts: React.FC<RelatedPromptsProps> = ({
  currentPrompt,
  allPrompts,
  maxItems = 5,
  onPromptClick
}) => {
  const [relatedPrompts, setRelatedPrompts] = useState<Prompt[]>([]);

  useEffect(() => {
    // 根据分类、标签和内容相似度计算相关提示词
    const calculateRelevance = (prompt: Prompt): number => {
      if (prompt.id === currentPrompt.id) return -1;

      let score = 0;

      // 相同分类得分最高
      if (prompt.category === currentPrompt.category) {
        score += 10;
      }

      // 相同标签得分
      const currentTags = currentPrompt.tags || [];
      const promptTags = prompt.tags || [];
      const commonTags = currentTags.filter(tag => 
        promptTags.some(t => t.toLowerCase() === tag.toLowerCase())
      );
      score += commonTags.length * 5;

      // 标题相似度（简单的关键词匹配）
      const currentWords = currentPrompt.title.toLowerCase().split(/\s+/);
      const promptWords = prompt.title.toLowerCase().split(/\s+/);
      const commonWords = currentWords.filter(word => 
        word.length > 2 && promptWords.includes(word)
      );
      score += commonWords.length * 3;

      // 内容相似度（简单的关键词匹配）
      const contentWords = currentPrompt.content.toLowerCase().split(/\s+/);
      const targetContentWords = prompt.content.toLowerCase().split(/\s+/);
      const commonContentWords = contentWords.filter(word => 
        word.length > 3 && targetContentWords.includes(word)
      ).slice(0, 10); // 限制匹配数量
      score += commonContentWords.length;

      return score;
    };

    // 计算所有提示词的相关性得分
    const scored = allPrompts
      .map(prompt => ({
        prompt,
        score: calculateRelevance(prompt)
      }))
      .filter(item => item.score > 0)
      .sort((a, b) => b.score - a.score)
      .slice(0, maxItems)
      .map(item => item.prompt);

    setRelatedPrompts(scored);
  }, [currentPrompt, allPrompts, maxItems]);

  const handlePromptClick = (prompt: Prompt) => {
    if (onPromptClick) {
      onPromptClick(prompt);
    }
  };

  if (relatedPrompts.length === 0) {
    return null;
  }

  return (
    <div className="mt-8 pt-8 border-t border-gray-200 dark:border-gray-700">
      <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4 flex items-center">
        <i className="fas fa-link mr-2 text-primary"></i>
        相关提示词推荐
      </h3>
      
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {relatedPrompts.map((prompt, index) => (
          <motion.div
            key={prompt.id}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
          >
            <button
              onClick={() => handlePromptClick(prompt)}
              className="block w-full text-left p-4 bg-gray-50 dark:bg-gray-800 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors group focus:outline-none focus:ring-2 focus:ring-primary focus:ring-opacity-50"
              title={`查看提示词：${prompt.title}`}
            >
              <h4 className="font-semibold text-gray-900 dark:text-white group-hover:text-primary transition-colors line-clamp-1">
                {prompt.title}
              </h4>
              
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1 line-clamp-2">
                {prompt.content}
              </p>
              
              <div className="mt-2 flex items-center justify-between">
                <span className="text-xs text-gray-500 dark:text-gray-500">
                  {prompt.category}
                </span>
                
                {prompt.tags && prompt.tags.length > 0 && (
                  <div className="flex gap-1">
                    {prompt.tags.slice(0, 2).map((tag, idx) => (
                      <span
                        key={idx}
                        className="px-2 py-1 text-xs bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </button>
          </motion.div>
        ))}
      </div>

      {/* SEO优化的内部链接 - 如果在广场页面，保留分类链接功能 */}
      <div className="mt-6 text-center">
        <span className="text-gray-600 dark:text-gray-400 text-sm">
          <i className="fas fa-lightbulb mr-2"></i>
          发现了 {relatedPrompts.length} 个相关提示词
        </span>
      </div>
    </div>
  );
};

// 分类相关提示词组件
export const CategoryPrompts: React.FC<{
  category: string;
  prompts: Prompt[];
  currentPromptId?: string;
  maxItems?: number;
  onPromptClick?: (prompt: Prompt) => void;
}> = ({ category, prompts, currentPromptId, maxItems = 6, onPromptClick }) => {
  const categoryPrompts = prompts
    .filter(p => p.category === category && (!currentPromptId || p.id.toString() !== currentPromptId.toString()))
    .slice(0, maxItems);

  const handlePromptClick = (prompt: Prompt) => {
    if (onPromptClick) {
      onPromptClick(prompt);
    }
  };

  if (categoryPrompts.length === 0) {
    return null;
  }

  return (
    <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-6">
      <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4">
        更多{category}提示词
      </h3>
      
      <div className="space-y-3">
        {categoryPrompts.map(prompt => (
          <button
            key={prompt.id}
            onClick={() => handlePromptClick(prompt)}
            className="block w-full text-left text-gray-700 dark:text-gray-300 hover:text-primary dark:hover:text-primary transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-opacity-50 rounded p-1"
            title={prompt.title}
          >
            <span className="line-clamp-1">{prompt.title}</span>
          </button>
        ))}
      </div>
    </div>
  );
};

export default RelatedPrompts;